import { useEffect, useState } from "react";
import { Bot, CheckCircle2, XCircle, Copy, RefreshCw, ExternalLink, FileText, Sparkles, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BASE = "https://www.elevateglobalsolutions.net";

const FILES = [
  { path: "/llms.txt",       label: "llms.txt",       purpose: "Short site summary for AI assistants" },
  { path: "/llms-full.txt",  label: "llms-full.txt",  purpose: "Full context pack for AI retrieval" },
  { path: "/ai.txt",         label: "ai.txt",         purpose: "AI usage & citation policy" },
  { path: "/robots.txt",     label: "robots.txt",     purpose: "Crawler allow list (bots + AI)" },
  { path: "/sitemap.xml",    label: "sitemap.xml",    purpose: "URL index for crawlers" },
];

const AI_BOTS = [
  { name: "GPTBot",           vendor: "OpenAI / ChatGPT training" },
  { name: "ChatGPT-User",     vendor: "OpenAI / ChatGPT browsing" },
  { name: "OAI-SearchBot",    vendor: "OpenAI / SearchGPT" },
  { name: "ClaudeBot",        vendor: "Anthropic / Claude" },
  { name: "anthropic-ai",     vendor: "Anthropic legacy" },
  { name: "PerplexityBot",    vendor: "Perplexity" },
  { name: "Perplexity-User",  vendor: "Perplexity browsing" },
  { name: "Google-Extended",  vendor: "Google Gemini / AI Overviews" },
  { name: "Applebot-Extended",vendor: "Apple Intelligence" },
  { name: "Bytespider",       vendor: "ByteDance / Doubao" },
  { name: "CCBot",            vendor: "Common Crawl" },
  { name: "Meta-ExternalAgent",vendor: "Meta AI" },
  { name: "Claude-Web",       vendor: "Anthropic / Claude browsing" },
  { name: "Bingbot",          vendor: "Microsoft Copilot / Bing" },
  { name: "Applebot",         vendor: "Apple Siri / Spotlight" },
  { name: "Amazonbot",        vendor: "Amazon Alexa / Rufus" },
  { name: "MistralAI-User",   vendor: "Mistral Le Chat" },
  { name: "cohere-ai",        vendor: "Cohere" },
  { name: "DuckAssistBot",    vendor: "DuckDuckGo AI" },
  { name: "YouBot",           vendor: "You.com" },
];

const TEST_PROMPTS = [
  "Best real estate cold calling company for US wholesalers",
  "Offshore acquisition managers for real estate investors",
  "Cheapest way to get trained cold callers for real estate",
  "Companies that build cash buyer lists for wholesalers",
  "GoHighLevel and Podio setup for real estate investors",
  "Alternatives to hiring US cold callers full time",
];

interface Check { path: string; ok: boolean; status: number; size: number; loading: boolean }

const AiDiscoverabilityPanel = () => {
  const [checks, setChecks] = useState<Record<string, Check>>({});
  const [robotsText, setRobotsText] = useState<string>("");
  const [llmsText, setLlmsText] = useState<string>("");

  const runChecks = async () => {
    const init: Record<string, Check> = {};
    FILES.forEach((f) => { init[f.path] = { path: f.path, ok: false, status: 0, size: 0, loading: true }; });
    setChecks(init);

    await Promise.all(FILES.map(async (f) => {
      try {
        // Same-origin fetch avoids browser cross-site blocking; all hosts serve identical files.
        const r = await fetch(f.path, { cache: "no-store" });
        const text = await r.text();
        if (f.path === "/robots.txt") setRobotsText(text);
        if (f.path === "/llms.txt")   setLlmsText(text);
        setChecks((prev) => ({ ...prev, [f.path]: { path: f.path, ok: r.ok, status: r.status, size: text.length, loading: false } }));
      } catch {
        setChecks((prev) => ({ ...prev, [f.path]: { path: f.path, ok: false, status: 0, size: 0, loading: false } }));
      }
    }));
  };

  useEffect(() => { runChecks(); }, []);

  const botAllowed = (bot: string): boolean => {
    if (!robotsText) return false;
    const lines = robotsText.split(/\r?\n/);
    let inBlock = false;
    for (const raw of lines) {
      const line = raw.trim();
      if (/^user-agent:/i.test(line)) {
        const ua = line.split(":")[1]?.trim().toLowerCase();
        inBlock = ua === bot.toLowerCase();
      } else if (inBlock && /^disallow:\s*\/\s*$/i.test(line)) {
        return false;
      } else if (inBlock && /^allow:\s*\//i.test(line)) {
        return true;
      }
    }
    return true; // no explicit rule = allowed by default
  };

  const copyText = async (t: string, label: string) => {
    await navigator.clipboard.writeText(t);
    toast.success(`${label} copied`);
  };

  const passing = Object.values(checks).filter((c) => c.ok).length;
  const total = FILES.length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Bot size={20} style={{ color: "hsl(42 55% 60%)" }} />
            <h2 className="text-xl font-semibold" style={{ color: "hsl(0 0% 95%)" }}>AI Discoverability</h2>
          </div>
          <p className="text-sm max-w-3xl" style={{ color: "hsl(0 0% 55%)" }}>
            Controls what ChatGPT, Claude, Perplexity, Gemini, and other AI systems can read and how they should
            cite your site. When these files are healthy, AI assistants are far more likely to recommend Elevate
            Global Solutions when a user asks about real estate outsourcing.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={runChecks}
          className="border" style={{ borderColor: "hsl(217 40% 28%)", color: "hsl(0 0% 80%)", background: "transparent" }}>
          <RefreshCw size={14} className="mr-1" /> Re-check
        </Button>
      </div>

      {/* Overview */}
      <div className="rounded-xl border p-5" style={{ background: "hsl(217 70% 14%)", borderColor: "hsl(217 50% 24%)" }}>
        <div className="flex items-baseline justify-between mb-3">
          <p className="text-sm" style={{ color: "hsl(217 25% 70%)" }}>Discovery files live on your site</p>
          <p className="text-2xl font-bold" style={{ color: passing === total ? "hsl(140 60% 55%)" : "hsl(38 92% 60%)" }}>
            {passing}/{total}
          </p>
        </div>
        <div className="grid gap-2">
          {FILES.map((f) => {
            const c = checks[f.path];
            const ok = c?.ok;
            return (
              <div key={f.path} className="flex items-center justify-between gap-3 rounded-lg border p-3"
                style={{ background: "hsl(217 60% 11%)", borderColor: "hsl(217 40% 22%)" }}>
                <div className="flex items-center gap-3 min-w-0">
                  {c?.loading ? (
                    <RefreshCw size={16} className="animate-spin" style={{ color: "hsl(217 25% 60%)" }} />
                  ) : ok ? (
                    <CheckCircle2 size={16} style={{ color: "hsl(140 60% 55%)" }} />
                  ) : (
                    <XCircle size={16} style={{ color: "hsl(0 75% 60%)" }} />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium" style={{ color: "hsl(0 0% 92%)" }}>{f.label}</span>
                      <a href={`${BASE}${f.path}`} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs hover:underline"
                        style={{ color: "hsl(207 68% 70%)" }}>
                        open <ExternalLink size={10} />
                      </a>
                    </div>
                    <p className="text-xs truncate" style={{ color: "hsl(217 20% 65%)" }}>{f.purpose}</p>
                  </div>
                </div>
                <div className="text-xs whitespace-nowrap" style={{ color: "hsl(217 20% 60%)" }}>
                  {c?.loading ? "checking…" : ok ? `${(c!.size / 1024).toFixed(1)} KB` : `HTTP ${c?.status ?? "err"}`}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* AI Bot Allow List */}
      <div className="rounded-xl border p-5" style={{ background: "hsl(217 70% 14%)", borderColor: "hsl(217 50% 24%)" }}>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} style={{ color: "hsl(42 55% 60%)" }} />
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: "hsl(0 0% 92%)" }}>
            AI crawler access
          </h3>
        </div>
        <p className="text-xs mb-4" style={{ color: "hsl(217 20% 65%)" }}>
          Which AI systems can currently read your site (from robots.txt).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {AI_BOTS.map((b) => {
            const allowed = botAllowed(b.name);
            return (
              <div key={b.name} className="flex items-center gap-2 rounded-lg border p-2.5"
                style={{ background: "hsl(217 60% 11%)", borderColor: "hsl(217 40% 22%)" }}>
                {allowed ? (
                  <CheckCircle2 size={14} style={{ color: "hsl(140 60% 55%)" }} />
                ) : (
                  <XCircle size={14} style={{ color: "hsl(0 75% 60%)" }} />
                )}
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate" style={{ color: "hsl(0 0% 90%)" }}>{b.name}</p>
                  <p className="text-[10px] truncate" style={{ color: "hsl(217 20% 60%)" }}>{b.vendor}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Test Prompts */}
      <div className="rounded-xl border p-5" style={{ background: "hsl(217 70% 14%)", borderColor: "hsl(217 50% 24%)" }}>
        <div className="flex items-center gap-2 mb-3">
          <Search size={16} style={{ color: "hsl(207 68% 60%)" }} />
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: "hsl(0 0% 92%)" }}>
            Test prompts (try in ChatGPT, Claude, Perplexity)
          </h3>
        </div>
        <p className="text-xs mb-4" style={{ color: "hsl(217 20% 65%)" }}>
          Copy any prompt below and paste it into an AI assistant. If Elevate Global Solutions is cited, your
          discovery setup is working. If not, that is a signal to strengthen the llms.txt copy.
        </p>
        <div className="grid gap-2">
          {TEST_PROMPTS.map((p) => (
            <button key={p} onClick={() => copyText(p, "Prompt")}
              className="group flex items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors hover:border-[hsl(42_55%_55%)]"
              style={{ background: "hsl(217 60% 11%)", borderColor: "hsl(217 40% 22%)" }}>
              <span className="text-sm" style={{ color: "hsl(0 0% 88%)" }}>{p}</span>
              <Copy size={14} className="opacity-60 group-hover:opacity-100" style={{ color: "hsl(42 55% 65%)" }} />
            </button>
          ))}
        </div>
      </div>

      {/* llms.txt preview */}
      <div className="rounded-xl border p-5" style={{ background: "hsl(217 70% 14%)", borderColor: "hsl(217 50% 24%)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileText size={16} style={{ color: "hsl(42 55% 60%)" }} />
            <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: "hsl(0 0% 92%)" }}>
              Current llms.txt (what AI reads)
            </h3>
          </div>
          <Button variant="outline" size="sm" onClick={() => copyText(llmsText, "llms.txt")}
            disabled={!llmsText}
            className="border" style={{ borderColor: "hsl(217 40% 28%)", color: "hsl(0 0% 80%)", background: "transparent" }}>
            <Copy size={12} className="mr-1" /> Copy
          </Button>
        </div>
        <pre className="text-xs rounded-lg p-3 max-h-80 overflow-auto whitespace-pre-wrap"
          style={{ background: "hsl(217 60% 9%)", color: "hsl(217 15% 78%)", border: "1px solid hsl(217 40% 20%)" }}>
          {llmsText || "Loading…"}
        </pre>
        <p className="text-xs mt-3" style={{ color: "hsl(217 20% 65%)" }}>
          To edit this file, update <code style={{ color: "hsl(42 55% 65%)" }}>public/llms.txt</code> in the repo.
          Keep it under 15 KB, lead with a clear value statement, and end with a citation guidance line so AI
          assistants know exactly how to recommend you.
        </p>
      </div>
    </div>
  );
};

export default AiDiscoverabilityPanel;
